import { useState, useEffect } from 'react';
import { commentService, Comment } from '../utils/commentService';
import { authService } from '../utils/auth';
import Avatar from './Avatar.tsx';
import '../styles/comments.css';

interface CommentSectionProps {
  photoId: string;
}

const CommentSection: React.FC<CommentSectionProps> = ({ photoId }) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [lastKey, setLastKey] = useState<string | undefined>();
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');

  const currentUser = authService.getCurrentUser();

  useEffect(() => {
    loadComments();
  }, [photoId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadComments = async (reset = true) => {
    if (isLoading) return;

    setIsLoading(true);
    try {
      const response = await commentService.getComments(
        photoId,
        20,
        reset ? undefined : lastKey
      );

      const responseComments = response.comments || [];

      if (reset) {
        setComments(responseComments);
      } else {
        setComments(prev => [...prev, ...responseComments]);
      }

      setHasMore(response.hasMore);
      setLastKey(response.lastKey);
    } catch (error) {
      console.error('Failed to load comments:', error);
      if (reset) {
        setComments([]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const comment = await commentService.createComment(
        photoId,
        newComment.trim()
      );
      setComments(prev => [...prev, comment]);
      setNewComment('');
    } catch (error) {
      console.error('Failed to post comment:', error);
      alert('コメントの投稿に失敗しました');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (comment: Comment) => {
    setEditingCommentId(comment.id);
    setEditContent(comment.content);
  };

  const handleSaveEdit = async (commentId: string) => {
    if (!editContent.trim()) return;

    try {
      const updatedComment = await commentService.updateComment(
        commentId,
        editContent.trim()
      );
      setComments(prev =>
        prev.map(comment =>
          comment.id === commentId ? updatedComment : comment
        )
      );
      setEditingCommentId(null);
      setEditContent('');
    } catch (error) {
      console.error('Failed to update comment:', error);
      alert('コメントの更新に失敗しました');
    }
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditContent('');
  };

  const handleDelete = async (commentId: string) => {
    if (!window.confirm('このコメントを削除しますか？')) return;

    try {
      await commentService.deleteComment(commentId);
      setComments(prev => prev.filter(comment => comment.id !== commentId));
    } catch (error) {
      console.error('Failed to delete comment:', error);
      alert('コメントの削除に失敗しました');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMinutes < 1) return 'たった今';
    if (diffMinutes < 60) return `${diffMinutes}分前`;
    if (diffHours < 24) return `${diffHours}時間前`;
    if (diffDays < 7) return `${diffDays}日前`;

    return date.toLocaleDateString('ja-JP');
  };

  return (
    <div className="comment-section">
      <div className="comment-header">
        <h3>コメント ({comments?.length || 0})</h3>
      </div>

      <div className="comment-list">
        {comments.map(comment => (
          <div key={comment.id} className="comment-item">
            <div className="comment-avatar">
              <Avatar size="small" avatarUrl={comment.avatar_url} />
            </div>
            <div className="comment-content">
              <div className="comment-meta">
                <span className="comment-username">{comment.username}</span>
                <span className="comment-date">
                  {formatDate(comment.created_at)}
                </span>
              </div>

              {editingCommentId === comment.id ? (
                <div className="comment-edit">
                  <textarea
                    value={editContent}
                    onChange={e => setEditContent(e.target.value)}
                    className="comment-edit-input"
                    rows={3}
                  />
                  <div className="comment-edit-actions">
                    <button
                      onClick={() => handleSaveEdit(comment.id)}
                      className="btn btn-primary btn-sm"
                      disabled={!editContent.trim()}
                    >
                      保存
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      className="btn btn-secondary btn-sm"
                    >
                      キャンセル
                    </button>
                  </div>
                </div>
              ) : (
                <div className="comment-text">
                  <p>{comment.content}</p>
                  {currentUser?.username === comment.username && (
                    <div className="comment-actions">
                      <button
                        onClick={() => handleEdit(comment)}
                        className="comment-action-btn"
                      >
                        編集
                      </button>
                      <button
                        onClick={() => handleDelete(comment.id)}
                        className="comment-action-btn delete"
                      >
                        削除
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}

        {hasMore && (
          <button
            onClick={() => loadComments(false)}
            className="load-more-comments"
            disabled={isLoading}
          >
            {isLoading ? '読み込み中...' : 'もっと見る'}
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit} className="comment-form">
        <textarea
          value={newComment}
          onChange={e => setNewComment(e.target.value)}
          placeholder="コメントを入力..."
          className="comment-input"
          rows={3}
          maxLength={500}
        />
        <div className="comment-form-footer">
          <span className="comment-counter">{newComment.length}/500</span>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={!newComment.trim() || isSubmitting}
          >
            {isSubmitting ? '投稿中...' : 'コメント'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CommentSection;
